import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSyncExternalStore, type ReactNode } from 'react';
import { api } from '../core/api.js';
import { avatarArt, initialsOf } from '../core/avatar-art.js';
import {
  avatarPreviewUrl, avatarPreviewsVersion, subscribeAvatarPreviews,
} from './avatar-preview.js';

interface UserAvatarProps {
  fullName: string;
  avatarUrl?: string | null;
  avatarFileId?: string | null;
  className?: string;
  decorative?: boolean;
  bare?: boolean;
}

/**
 * Nombre de sondages avant d'abandonner — 36 × 5 s, soit trois minutes.
 * Une analyse qui n'a pas abouti au bout de trois minutes n'aboutira pas toute
 * seule : le worker est arrêté ou la file est bloquée. Continuer à interroger
 * en silence fabriquerait une attente rassurante et fausse, ce que le produit
 * interdit. On s'arrête, et l'infobulle le dit.
 */
const SONDAGES_MAX = 36;

const EN_ANALYSE = 'Photo en cours d’analyse antivirus';
const ANALYSE_BLOQUEE =
  'Analyse antivirus toujours pas terminée : la photo reste indisponible.';

export function UserAvatar({
  fullName, avatarUrl, avatarFileId, className = 'avatar', decorative = true,
  bare = false,
}: UserAvatarProps): ReactNode {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['user-avatar-status', avatarFileId],
    queryFn: () => api<{ scan_status: string }>(`/files/${avatarFileId}/status`),
    enabled: Boolean(avatarFileId),
    staleTime: 5_000,
    refetchInterval: (query) => {
      if (query.state.data?.scan_status !== 'en_quarantaine') return false;
      // `dataUpdateCount` compte les réponses reçues : c'est la seule mesure
      // d'ancienneté qui survive au fait que chaque sondage rafraîchit l'heure
      // de la donnée (un écart « maintenant − dataUpdatedAt » vaudrait toujours
      // zéro juste après un sondage, et n'expirerait donc jamais).
      return query.state.dataUpdateCount >= SONDAGES_MAX ? false : 5_000;
    },
  });
  const local = useQuery({
    queryKey: ['user-avatar-file', avatarFileId],
    queryFn: () => api<{ url: string }>(`/files/${avatarFileId}`),
    enabled: Boolean(avatarFileId && status.data?.scan_status === 'sain'),
    staleTime: 50 * 60_000,
    refetchInterval: false,
  });

  /*
   * Aperçu LOCAL pendant l'analyse. Le serveur ne sert un fichier qu'une fois
   * déclaré sain : entre l'envoi et le verdict, la pastille restait sur les
   * initiales, exactement comme si l'envoi avait échoué — au point qu'on
   * recharge la page en croyant à un défaut de rafraîchissement. L'image
   * choisie s'affiche donc tout de suite, avec un état provisoire visible.
   */
  useSyncExternalStore(subscribeAvatarPreviews, avatarPreviewsVersion, avatarPreviewsVersion);
  const enAnalyse = status.data?.scan_status === 'en_quarantaine';
  // Le compteur de réponses vit dans le cache, pas dans le résultat du hook :
  // c'est lui qui dit si l'on a déjà renoncé à sonder.
  const sondages = queryClient
    .getQueryState(['user-avatar-status', avatarFileId])?.dataUpdateCount ?? 0;
  const abandonnee = enAnalyse && sondages >= SONDAGES_MAX;
  const apercu = enAnalyse ? avatarPreviewUrl(avatarFileId) : undefined;

  const src = local.data?.url ?? apercu ?? avatarUrl ?? undefined;
  const label = decorative ? undefined : `Photo de ${fullName}`;
  const marque = enAnalyse ? 'avatar-scan' : undefined;
  const infobulle = enAnalyse ? (abandonnee ? ANALYSE_BLOQUEE : EN_ANALYSE) : undefined;

  const content = src
    ? <img src={src} alt={decorative ? '' : label} referrerPolicy="no-referrer"
        className={marque} title={infobulle} />
    // Sans photo : visuel coloré propre au nom, plutôt qu'une pastille grise identique pour tous.
    : <span className={`avatar-art${marque ? ` ${marque}` : ''}`} style={{ '--avatar-art': avatarArt(fullName) }}
        // Initiales dessinées par la feuille de style : décor de la pastille, jamais
        // un texte qui contredirait le nom d'un bouton qui la contient (WCAG 2.5.3).
        title={infobulle} data-initials={initialsOf(fullName)} />;

  if (bare) return content;
  return <span className={className} aria-hidden={decorative || undefined} aria-label={label}>
    {content}
  </span>;
}
